import React from 'react';
import {
    Box, Container, Heading, Text, VStack, HStack, Button, Icon,
    SimpleGrid, List, ListItem, ListIcon,
} from '@chakra-ui/react';
import { FiCheck, FiPlus, FiX } from 'react-icons/fi';
import { FaShoppingBag, FaWeight, FaTshirt } from 'react-icons/fa';
import { freePickupMiles } from '../../utils/deliveryCopy';

export default function SitePricing({ config }) {
    const laundryId = config?.laundryId || '1';
    const sc = config?.siteContent || {};
    // Tenant-honest pickup feature: advertise the free-mileage band if the
    // tenant configured one; otherwise a neutral line (no blanket "free" claim).
    const _freeMiles = freePickupMiles(config);
    const _pickupFeature = _freeMiles
        ? `Pickup included within the first ${_freeMiles % 1 === 0 ? _freeMiles : _freeMiles.toFixed(1)} miles`
        : 'Pickup & delivery available';
    const themeColor = sc.themeColor || 'blue';
    const services = config?.services || config?.laundryServices || [];
    const serviceCategories = config?.serviceCategories || [];

    // Per-category "hide prices on the public card" (site_content.hidePriceCategoryIds:
    // array of category ids). When a card's category is listed, the price header
    // is hidden and its feature list shows item names only (no "— $X"). Lets a
    // tenant present e.g. a "Bulky & Specialty Items" card as a plain list. A
    // global sc.hidePricesAllCards hides prices on every card.
    const _hidePriceIds = new Set(
        (Array.isArray(sc.hidePriceCategoryIds) ? sc.hidePriceCategoryIds : []).map((v) => String(v))
    );
    const hidePriceForCategory = (categoryId) =>
        !!sc.hidePricesAllCards || _hidePriceIds.has(String(categoryId));
    // Strip a trailing "— $12.34" / "— $12.34/lb" price from a feature label.
    const stripPrice = (label) => String(label).replace(/\s*[—-]\s*\$[\d.,]+(\/lb)?\s*$/, '').trim();

    // Tenant-configured bag size guide + disclaimer (display only). When set,
    // the per-bag / flat-rate pricing card uses the configured guide rows as its
    // feature list (instead of the auto-generated per-service list) and shows the
    // disclaimer inside that card. Hidden entirely when not configured.
    const bagSizeGuide = Array.isArray(config?.bagSizeGuide) ? config.bagSizeGuide : [];
    const bagSizeDisclaimer = (config?.bagSizeDisclaimer || '').trim();
    const hasBagGuide = bagSizeGuide.length > 0;
    // Build the feature strings from the configured guide (e.g.
    // "Medium — 13-20 gallons — $30"). Used to replace a bag card's features.
    const overageRate = Number(config?.bagOverageRate);
    const overageStr = Number.isFinite(overageRate) && overageRate > 0
        ? `Extra weight $${overageRate.toFixed(2)}/lb over the cap`
        : '';
    const bagGuideFeatures = bagSizeGuide.map((row) => {
        const cap = (row.capacity || '').toString().trim();
        const price = (row.price || '').toString().trim();
        const priceStr = price ? (price.startsWith('$') ? price : `$${price}`) : '';
        const weight = parseFloat(row.weightCap || row.weight_cap);
        const weightStr = Number.isFinite(weight) && weight > 0 ? `up to ${weight} lb` : '';
        return [row.label, cap, weightStr, priceStr].filter(Boolean).join(' — ');
    });
    if (overageStr && bagGuideFeatures.length) bagGuideFeatures.push(overageStr);

    // If categories exist, group services by category
    if (serviceCategories.length > 0) {
        // Build one card per category that has customer-visible services
        const pricingCards = [];
        const sortedCategories = [...serviceCategories].sort((a, b) => a.displayOrder - b.displayOrder);

        for (const cat of sortedCategories) {
            const catServices = services.filter(s => s.categoryId === cat.categoryId);
            if (catServices.length === 0) continue;

            const cheapest = catServices.reduce((min, s) => Math.min(min, parseFloat(s.price || 0)), Infinity);
            const hasWeight = catServices.some(s => s.inputWeight === true || s.inputWeight === 'true');
            const unit = hasWeight ? '/lb' : (catServices.length > 1 ? ' starting' : '');

            // The bag size guide replaces the feature list ONLY for the actual
            // per-bag category (identified by its name containing "bag"), not
            // every non-weight category — otherwise flat-rate categories like
            // "Comforters, Sarees" would wrongly show bag sizes.
            const isBagCard = !hasWeight && /bag/i.test(cat.categoryName || '');
            const useGuide = isBagCard && hasBagGuide;
            const hidePrice = hidePriceForCategory(cat.categoryId);
            const rawFeatures = useGuide
                ? bagGuideFeatures
                : catServices.slice(0, 5).map(s => {
                    const p = parseFloat(s.price);
                    const suffix = (s.inputWeight === true || s.inputWeight === 'true') ? '/lb' : '';
                    return `${s.serviceName} — $${p % 1 === 0 ? p : p.toFixed(2)}${suffix}`;
                });
            pricingCards.push({
                title: cat.categoryName,
                categoryId: cat.categoryId,
                hidePrice,
                price: `${cheapest % 1 === 0 ? cheapest : cheapest.toFixed(2)}`,
                unit,
                icon: hasWeight ? FaWeight : FaShoppingBag,
                popular: pricingCards.length === 0,
                description: useGuide
                    ? 'Choose the bag size that fits your load.'
                    : (catServices.length === 1
                        ? catServices[0].description || `${cat.categoryName} service`
                        : `${catServices.length} services available`),
                // When prices are hidden, show item names only (drop the "— $X").
                features: hidePrice ? rawFeatures.map(stripPrice) : rawFeatures,
                disclaimer: useGuide ? bagSizeDisclaimer : '',
                cta: 'Get Started',
            });
        }

        if (pricingCards.length === 0) {
            // Categories exist but none have services — fall through to legacy
        } else {
            return (
                <Box id="pricing" py={{ base: 16, md: 20 }} bg="white">
                    <Container maxW="1200px">
                        <VStack spacing={4} textAlign="center" mb={12}>
                            <Text fontSize="sm" fontWeight="600" color={`${themeColor}.500`} textTransform="uppercase" letterSpacing="wide">
                                Pricing
                            </Text>
                            <Heading fontSize={{ base: '2xl', md: '4xl' }} color="gray.800">
                                Simple, transparent pricing
                            </Heading>
                            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.500" maxW="500px">
                                No hidden fees, no contracts. Choose what works for you.
                            </Text>
                        </VStack>

                        <SimpleGrid columns={{ base: 1, md: Math.min(pricingCards.length, 3) }} spacing={6} maxW={pricingCards.length === 1 ? '450px' : '1000px'} mx="auto">
                            {pricingCards.map((plan, idx) => (
                                <Box key={plan.title} bg={idx === 0 ? `${themeColor}.50` : 'white'}
                                    border="2px solid" borderColor={idx === 0 ? `${themeColor}.300` : 'gray.200'}
                                    borderRadius="2xl" p={{ base: 6, md: 8 }} position="relative"
                                    transition="all 0.3s" _hover={{ boxShadow: 'xl', transform: 'translateY(-4px)' }}>
                                    <VStack spacing={5} align="stretch">
                                        <HStack spacing={3}>
                                            <Box bg={idx === 0 ? `${themeColor}.100` : 'gray.100'} borderRadius="lg" p={2}>
                                                <Icon as={plan.icon} boxSize={5} color={idx === 0 ? `${themeColor}.500` : 'gray.500'} />
                                            </Box>
                                            <Text fontSize="lg" fontWeight="700" color="gray.800">{plan.title}</Text>
                                        </HStack>
                                        {!plan.hidePrice && (
                                            <HStack align="baseline" spacing={1}>
                                                <Text fontSize="4xl" fontWeight="800" color="gray.800">${plan.price}</Text>
                                                <Text fontSize="md" color="gray.500" fontWeight="500">{plan.unit}</Text>
                                            </HStack>
                                        )}
                                        <Text fontSize="sm" color="gray.500">{plan.description}</Text>
                                        <List spacing={3}>
                                            {plan.features.map((feature) => (
                                                <ListItem key={feature} fontSize="sm" color="gray.600" display="flex" alignItems="center">
                                                    <ListIcon as={FiCheck} color="green.400" boxSize={4} />
                                                    {feature}
                                                </ListItem>
                                            ))}
                                        </List>
                                        {plan.disclaimer && (
                                            <Text fontSize="xs" color="gray.500" lineHeight="short">{plan.disclaimer}</Text>
                                        )}
                                        <Button as="a" href={`/${laundryId}`} colorScheme={idx === 0 ? themeColor : 'gray'}
                                            variant={idx === 0 ? 'solid' : 'outline'} size="lg" borderRadius="full" mt={2}>
                                            {plan.cta}
                                        </Button>
                                    </VStack>
                                </Box>
                            ))}
                        </SimpleGrid>

                        {renderPricingNotice(sc)}
                        {renderInfoCards(sc, themeColor)}

                        {!sc.hideSelfService && (
                            <Box mt={10} bg="gray.50" borderRadius="2xl" p={{ base: 5, md: 6 }} textAlign="center" maxW="600px" mx="auto">
                                <Text fontWeight="600" color="gray.700" mb={1}>Prefer to do it yourself?</Text>
                                <Text fontSize="sm" color="gray.500">
                                    {sc.selfServiceNote || 'Visit our self-service laundromat with modern washers and dryers.'}
                                </Text>
                            </Box>
                        )}
                    </Container>
                </Box>
            );
        }
    }

    // ─── Fallback: Legacy per-pound / per-piece logic ────────────────────────
    // Determine what pricing types exist from actual services
    const perPoundServices = services.filter(s => s.inputWeight === true || s.inputWeight === 'true');
    const perPieceServices = services.filter(s => !s.inputWeight || s.inputWeight === false || s.inputWeight === 'false');
    const hasPerPound = perPoundServices.length > 0;
    const hasPerPiece = perPieceServices.length > 0;

    // Build pricing cards dynamically
    const pricingCards = [];

    if (hasPerPiece) {
        // Show per-piece/bag services as a card
        const cheapest = perPieceServices.reduce((min, s) => Math.min(min, parseFloat(s.price || 0)), Infinity);
        // Only apply the bag guide here if the flat-rate services are actually
        // bag services (a name contains "bag"). Prevents non-bag flat-rate
        // services from wrongly showing bag sizes.
        const piecesAreBags = perPieceServices.some(s => /bag/i.test(s.serviceName || ''));
        const useBagGuideLegacy = hasBagGuide && piecesAreBags;
        const _legacyPieceFeatures = useBagGuideLegacy
            ? bagGuideFeatures
            : perPieceServices.slice(0, 5).map(s => `${s.serviceName} — $${parseFloat(s.price).toFixed(2)}`);
        pricingCards.push({
            title: perPieceServices.length === 1 ? perPieceServices[0].serviceName : 'Flat Rate Services',
            hidePrice: !!sc.hidePricesAllCards,
            price: `$${cheapest % 1 === 0 ? cheapest : cheapest.toFixed(2)}`,
            unit: perPieceServices.length > 1 ? ' starting' : '',
            icon: FaShoppingBag,
            popular: !hasPerPound, // Popular if it's the only option
            description: useBagGuideLegacy
                ? 'Choose the bag size that fits your load.'
                : (perPieceServices.length === 1
                    ? perPieceServices[0].description || 'Simple flat rate pricing.'
                    : 'Choose from our flat-rate services.'),
            features: sc.hidePricesAllCards ? _legacyPieceFeatures.map(stripPrice) : _legacyPieceFeatures,
            disclaimer: useBagGuideLegacy ? bagSizeDisclaimer : '',
            cta: 'Get Started',
        });
    }

    if (hasPerPound) {
        const cheapestLb = perPoundServices.reduce((min, s) => Math.min(min, parseFloat(s.price || 0)), Infinity);
        const _legacyPoundFeatures = [
            ...perPoundServices.slice(0, 3).map(s => `${s.serviceName} — $${parseFloat(s.price).toFixed(2)}/lb`),
            _pickupFeature,
            'Same-day turnaround available',
        ];
        pricingCards.push({
            title: 'Per Pound',
            hidePrice: !!sc.hidePricesAllCards,
            price: `$${cheapestLb.toFixed(2)}`,
            unit: '/lb',
            icon: FaWeight,
            popular: hasPerPound && !hasPerPiece, // Popular if only option
            description: 'Pay based on the actual weight of your laundry.',
            features: sc.hidePricesAllCards ? _legacyPoundFeatures.map(stripPrice) : _legacyPoundFeatures,
            cta: 'Get Started',
        });
    }

    // If no services configured, show a generic card
    if (pricingCards.length === 0) {
        pricingCards.push({
            title: 'Laundry Service',
            price: 'Contact Us',
            unit: '',
            icon: FaTshirt,
            popular: true,
            description: 'Professional laundry service.',
            features: ['Wash, dry & fold', _pickupFeature, 'Same-day available'],
            cta: 'Get Started',
        });
    }

    // Mark first card as popular if none are
    if (!pricingCards.some(c => c.popular)) pricingCards[0].popular = true;

    return (
        <Box id="pricing" py={{ base: 16, md: 20 }} bg="white">
            <Container maxW="1200px">
                <VStack spacing={4} textAlign="center" mb={12}>
                    <Text fontSize="sm" fontWeight="600" color={`${themeColor}.500`} textTransform="uppercase" letterSpacing="wide">
                        Pricing
                    </Text>
                    <Heading fontSize={{ base: '2xl', md: '4xl' }} color="gray.800">
                        Simple, transparent pricing
                    </Heading>
                    <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.500" maxW="500px">
                        No hidden fees, no contracts. Choose what works for you.
                    </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: pricingCards.length > 1 ? 2 : 1 }} spacing={6} maxW={pricingCards.length > 1 ? '800px' : '450px'} mx="auto">
                    {pricingCards.map((plan) => (
                        <Box key={plan.title} bg={plan.popular ? `${themeColor}.50` : 'white'}
                            border="2px solid" borderColor={plan.popular ? `${themeColor}.300` : 'gray.200'}
                            borderRadius="2xl" p={{ base: 6, md: 8 }} position="relative"
                            transition="all 0.3s" _hover={{ boxShadow: 'xl', transform: 'translateY(-4px)' }}>

                            <VStack spacing={5} align="stretch">
                                <HStack spacing={3}>
                                    <Box bg={plan.popular ? `${themeColor}.100` : 'gray.100'} borderRadius="lg" p={2}>
                                        <Icon as={plan.icon} boxSize={5} color={plan.popular ? `${themeColor}.500` : 'gray.500'} />
                                    </Box>
                                    <Text fontSize="lg" fontWeight="700" color="gray.800">{plan.title}</Text>
                                </HStack>
                                {!plan.hidePrice && (
                                    <HStack align="baseline" spacing={1}>
                                        <Text fontSize="4xl" fontWeight="800" color="gray.800">{plan.price}</Text>
                                        <Text fontSize="md" color="gray.500" fontWeight="500">{plan.unit}</Text>
                                    </HStack>
                                )}
                                <Text fontSize="sm" color="gray.500">{plan.description}</Text>
                                <List spacing={3}>
                                    {plan.features.map((feature) => (
                                        <ListItem key={feature} fontSize="sm" color="gray.600" display="flex" alignItems="center">
                                            <ListIcon as={FiCheck} color="green.400" boxSize={4} />
                                            {feature}
                                        </ListItem>
                                    ))}
                                </List>
                                {plan.disclaimer && (
                                    <Text fontSize="xs" color="gray.500" lineHeight="short">{plan.disclaimer}</Text>
                                )}
                                <Button as="a" href={`/${laundryId}`} colorScheme={plan.popular ? themeColor : 'gray'}
                                    variant={plan.popular ? 'solid' : 'outline'} size="lg" borderRadius="full" mt={2}>
                                    {plan.cta}
                                </Button>
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>

                {renderPricingNotice(sc)}
                {renderInfoCards(sc, themeColor)}

                {/* Self-service mention */}
                {!sc.hideSelfService && (
                    <Box mt={10} bg="gray.50" borderRadius="2xl" p={{ base: 5, md: 6 }} textAlign="center" maxW="600px" mx="auto">
                        <Text fontWeight="600" color="gray.700" mb={1}>Prefer to do it yourself?</Text>
                        <Text fontSize="sm" color="gray.500">
                            {sc.selfServiceNote || 'Visit our self-service laundromat with modern washers and dryers.'}
                        </Text>
                    </Box>
                )}
            </Container>
        </Box>
    );
}

// ── Optional pricing-section extras (global, configurable via site_content) ───
// A short notice line (e.g. "20-pound minimum") + up to three info cards:
// What's Included / Optional Add-Ons / Items We Cannot Accept. Each renders only
// when the tenant has configured it, so existing tenants see no change.

function renderPricingNotice(sc) {
    const notice = (sc?.pricingNotice || '').trim();
    if (!notice) return null;
    return (
        <Box mt={10} maxW="700px" mx="auto" bg="orange.50" border="1px solid" borderColor="orange.200"
            borderRadius="xl" px={{ base: 4, md: 5 }} py={3} textAlign="center">
            <Text fontSize="sm" fontWeight="600" color="orange.700">{notice}</Text>
        </Box>
    );
}

function renderInfoCards(sc, themeColor) {
    const included = Array.isArray(sc?.pricingIncluded) ? sc.pricingIncluded.filter(Boolean) : [];
    const addons = Array.isArray(sc?.pricingAddonsList) ? sc.pricingAddonsList.filter(Boolean) : [];
    const cannot = Array.isArray(sc?.pricingCannotAccept) ? sc.pricingCannotAccept.filter(Boolean) : [];

    const cards = [];
    if (included.length) cards.push({ title: "What's Included", items: included, icon: FiCheck, accent: 'green', included: true });
    if (addons.length) cards.push({ title: 'Optional Add-Ons', items: addons, icon: FiPlus, accent: themeColor, included: false });
    if (cannot.length) cards.push({ title: 'Items We Cannot Accept', items: cannot, icon: FiX, accent: 'red', included: false });
    if (cards.length === 0) return null;

    return (
        <SimpleGrid columns={{ base: 1, md: Math.min(cards.length, 3) }} spacing={6} mt={12} maxW="1000px" mx="auto">
            {cards.map((card) => (
                <Box key={card.title} bg="white" border="1px solid" borderColor="gray.200" borderRadius="2xl" p={{ base: 5, md: 6 }}>
                    <HStack spacing={3} mb={4}>
                        <Box bg={`${card.accent}.50`} borderRadius="lg" p={2}>
                            <Icon as={card.icon} boxSize={5} color={`${card.accent}.500`} />
                        </Box>
                        <Text fontSize="md" fontWeight="700" color="gray.800">{card.title}</Text>
                    </HStack>
                    <List spacing={2}>
                        {card.items.map((it, i) => (
                            <ListItem key={i} fontSize="sm" color="gray.600" display="flex" alignItems="flex-start">
                                <ListIcon as={card.included ? FiCheck : (card.accent === 'red' ? FiX : FiPlus)}
                                    color={`${card.accent}.400`} boxSize={4} mt="2px" />
                                <span>{it}</span>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            ))}
        </SimpleGrid>
    );
}
